import React, { useState } from 'react';
import { projectFirestore, timestamp } from '../../firebase/config';
import useSections from '../../hooks/useSections';

const SectionManager = () => {
    const { sections, loading } = useSections();
    const [newLabel, setNewLabel] = useState('');
    const [newKey, setNewKey] = useState('');
    const [saving, setSaving] = useState(false);
    const [reordering, setReordering] = useState(false);

    const generateKey = (label) => {
        return label
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9\u0621-\u064A-]/g, '')
            .substring(0, 40) || 'section-' + Date.now();
    };

    const handleAdd = async () => {
        const label = newLabel.trim();
        if (!label) return alert('أدخل اسم القسم');
        
        const key = newKey.trim() || generateKey(label);
        
        if (sections.find(s => s.key === key)) {
            return alert('يوجد قسم بنفس المعرّف: ' + key);
        }

        setSaving(true);
        try {
            const maxOrder = sections.length > 0 ? Math.max(...sections.map(s => s.order || 0)) : -1;
            await projectFirestore.collection('sections').doc(key).set({
                key,
                label,
                order: maxOrder + 1,
                enabled: true,
                defaultCover: '',
                createdAt: timestamp(),
            });
            setNewLabel('');
            setNewKey('');
        } catch (err) {
            alert('خطأ: ' + err.message);
        }
        setSaving(false);
    };

    const handleToggle = async (section) => {
        await projectFirestore.collection('sections').doc(section.key).update({
            enabled: !section.enabled,
        });
    };

    const handleRename = async (section) => {
        const newName = window.prompt('اسم القسم الجديد:', section.label);
        if (!newName || newName.trim() === section.label) return;
        await projectFirestore.collection('sections').doc(section.key).update({
            label: newName.trim(),
        });
    };

    const handleDelete = async (section) => {
        if (!window.confirm('حذف قسم "' + section.label + '"؟\n\nملاحظة: الصور الموجودة في هذا القسم ستبقى في قاعدة البيانات.')) return;
        await projectFirestore.collection('sections').doc(section.key).delete();
    };

    const handleMoveUp = async (index) => {
        if (index === 0) return;
        const current = sections[index];
        const prev = sections[index - 1];
        const batch = projectFirestore.batch();
        batch.update(projectFirestore.collection('sections').doc(current.key), { order: prev.order });
        batch.update(projectFirestore.collection('sections').doc(prev.key), { order: current.order });
        await batch.commit();
    };

    const handleMoveDown = async (index) => {
        if (index >= sections.length - 1) return;
        const current = sections[index];
        const next = sections[index + 1];
        const batch = projectFirestore.batch();
        batch.update(projectFirestore.collection('sections').doc(current.key), { order: next.order });
        batch.update(projectFirestore.collection('sections').doc(next.key), { order: current.order });
        await batch.commit();
    };

    const handleAutoOrder = async () => {
        setReordering(true);
        const batch = projectFirestore.batch();
        sections.forEach((s, i) => {
            batch.update(projectFirestore.collection('sections').doc(s.key), { order: i });
        });
        await batch.commit();
        setReordering(false);
    };

    if (loading) return <div className="text-center py-3"><div className="spinner-border spinner-border-sm"></div></div>;

    return (
        <div className="container my-4">
            <h4 className="text-center mb-4">📂 إدارة أقسام المعرض</h4>

            <div className="card shadow-sm mb-4">
                <div className="card-body">
                    <h6 className="card-title">إضافة قسم جديد</h6>
                    <div className="row g-2 align-items-end">
                        <div className="col-md-4">
                            <label className="form-label">اسم القسم (عربي)</label>
                            <input className="form-control" value={newLabel} onChange={e => setNewLabel(e.target.value)}
                                placeholder="مثال: الحياة البرية" />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">المعرّف (اختياري - إنجليزي)</label>
                            <input className="form-control" value={newKey} onChange={e => setNewKey(e.target.value)}
                                placeholder="مثال: wildlife" dir="ltr" />
                            <small className="text-muted">يُستخدم في الرابط. يُولّد تلقائياً إذا تُرك فارغاً</small>
                        </div>
                        <div className="col-md-4">
                            <button className="btn btn-success w-100" onClick={handleAdd} disabled={saving || !newLabel.trim()}>
                                {saving ? 'جاري الإضافة...' : '➕ إضافة قسم'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="badge bg-secondary">{sections.length} قسم</span>
                <button className="btn btn-sm btn-outline-info" onClick={handleAutoOrder} disabled={reordering}>
                    {reordering ? 'جاري...' : '🔢 إعادة ترقيم الترتيب'}
                </button>
            </div>

            <div className="list-group">
                {sections.map((section, index) => (
                    <div key={section.key} className={'list-group-item d-flex align-items-center gap-2' + (section.enabled === false ? ' bg-light opacity-75' : '')}>
                        <div className="btn-group btn-group-sm">
                            <button className="btn btn-outline-secondary" onClick={() => handleMoveUp(index)} disabled={index === 0}>▲</button>
                            <button className="btn btn-outline-secondary" onClick={() => handleMoveDown(index)} disabled={index >= sections.length - 1}>▼</button>
                        </div>
                        
                        <span className="badge bg-dark" style={{ fontSize: 10, minWidth: 25 }}>{index + 1}</span>
                        
                        <div className="flex-grow-1">
                            <strong>{section.label}</strong>
                            <small className="text-muted ms-2" dir="ltr">/{section.key}</small>
                        </div>

                        <div className="form-check form-switch">
                            <input className="form-check-input" type="checkbox" checked={section.enabled !== false}
                                onChange={() => handleToggle(section)} title={section.enabled !== false ? 'تعطيل' : 'تفعيل'} />
                        </div>

                        <button className="btn btn-sm btn-outline-primary" onClick={() => handleRename(section)} title="تغيير الاسم">✏</button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(section)} title="حذف">🗑</button>
                    </div>
                ))}
            </div>

            <div className="alert alert-info mt-4" style={{ fontSize: 13 }}>
                <strong>ملاحظات:</strong>
                <ul className="mb-0 mt-1">
                    <li>الأقسام الجديدة تظهر فوراً في القائمة والمعرض وصفحة الرفع</li>
                    <li>تعطيل القسم يخفيه من الزوار لكن يبقي الصور محفوظة</li>
                    <li>حذف القسم يزيل بياناته من القائمة فقط — الصور تبقى في قاعدة البيانات</li>
                    <li>المعرّف (key) يُستخدم كرابط الصفحة واسم المجموعة في قاعدة البيانات</li>
                </ul>
            </div>
        </div>
    );
};

export default SectionManager;
